/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 * Page dashboard translation constants
 *
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.34.0
 */




import { ReactElement } from 'react';

import documentsIcon from '@megafon/ui-icons/basic-16-archive_16.svg';
import messageIcon from '@megafon/ui-icons/basic-16-message_16.svg';
import billsIcon from '@megafon/ui-icons/basic-16-mobile-cash-desk_16.svg';
import promisePaymentIcon from '@megafon/ui-icons/basic-16-o-platezh_16.svg';
import roamingIcon from '@megafon/ui-icons/basic-16-roaming_16.svg';
import advancePaymentIcon from '@megafon/ui-icons/basic-16-transaction_16.svg';
import { Typography } from '@nexign/scc-ui-kit';
import { Dispatch } from 'redux';

import { TableFilterPresetQueryId } from '@/main/components/Table/types/tableTypes';
import { setLocalStorageDataAction } from '@/main/modules/storage/storageActions';
import { ELocalStorageKeys } from '@/main/modules/storage/storageTypes';
import { SUBSCRIBERS_IN_ROAMING } from '@/main/pages/PageSubscriberMobile/constants';
import { openRoute } from '@/main/routes/constants/externalRoutes';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import { ColorGrey3, ColorLink } from '@/main/theme/default/colors/palette';

import { EFastActionsButtonTypes } from './constants/constants';
import { IFastActionButtonProps } from './FastActionButton';
import { IFastActionsTranslation } from './types/fastActionsTypes';

interface IFastActionButtonsProps {
    translation: IFastActionsTranslation;
}

export const mapFastActionButtonTypeToUrl: { 
    [key in EFastActionsButtonTypes]: string 
} = {
    [EFastActionsButtonTypes.ADVANCE_INVOICE]: RoutePath.fastAdvanceInvoice,
    [EFastActionsButtonTypes.BILL_INVOICE]: RoutePath.wizardFastBillsDoc,
    [EFastActionsButtonTypes.INQUIRY_CREATE]: RoutePath.fastInquiryCreate,
    [EFastActionsButtonTypes.PROMISED_PAYMENTS]: RoutePath.fastPromisedPayment,
    [EFastActionsButtonTypes.REPORT_DOCUMENTS]: RoutePath.wizardFastReportDoc,
    [EFastActionsButtonTypes.SUBSCRIBERS_IN_ROAMING]: '',
};

export const getRoamSubsText = (
    subscribersInRoamingCount: number, 
    translation: IFastActionsTranslation,
    ): ReactElement => {
        return (
            <>
                <Typography variant="body2">{translation.fastActionsPlate.roamSubs}</Typography>
                {!!subscribersInRoamingCount 
                    ? <Typography color={ColorLink} variant="body2">{subscribersInRoamingCount}</Typography>
                    : <Typography color={ColorGrey3} variant="body2">{translation.fastActionsPlate.noRoamSubs}</Typography>
                }
            </>
        )
    };

export const openRoamSubscribersTable = (dispatch: Dispatch<any>) => {
    dispatch(
        setLocalStorageDataAction({
            key: ELocalStorageKeys.IS_SUBSCRIBERS_IN_ROAMING_PLATE_HIDDEN,
            value: false,
        }),
    );
    openRoute(`${RoutePath.subscriberMobile}?${TableFilterPresetQueryId}=${SUBSCRIBERS_IN_ROAMING}`);
};

export const buttonsArray = ({ translation }: IFastActionButtonsProps): IFastActionButtonProps[] => ([
    { 
        buttonType: EFastActionsButtonTypes.SUBSCRIBERS_IN_ROAMING, 
        buttonText: '', 
        buttonIcon: roamingIcon,
    },
    { 
        buttonType: EFastActionsButtonTypes.INQUIRY_CREATE, 
        buttonText: translation.fastActionsPlate.createInquiry, 
        buttonIcon: messageIcon, 
    },
    { 
        buttonType: EFastActionsButtonTypes.BILL_INVOICE, 
        buttonText: translation.fastActionsPlate.bills, 
        buttonIcon: billsIcon, 
    },
    { 
        buttonType: EFastActionsButtonTypes.REPORT_DOCUMENTS, 
        buttonText: translation.fastActionsPlate.documents, 
        buttonIcon: documentsIcon, 
    },
    { 
        buttonType: EFastActionsButtonTypes.ADVANCE_INVOICE, 
        buttonText: translation.fastActionsPlate.advanceAccount, 
        buttonIcon: advancePaymentIcon, 
    },
    { 
        buttonType: EFastActionsButtonTypes.PROMISED_PAYMENTS, 
        buttonText: translation.fastActionsPlate.addPromicePayment,
        buttonIcon: promisePaymentIcon,
    },
]);
